<template>
  <div>
    <router-view  v-if="isAppLoaded && !initialTranslationsPending" />
    <div v-else class="d-flex w-100 h-screen justify-center align-center">
      <v-progress-circular
      size="50"
      indeterminate
      color="primary-main"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, computed, ref, onMounted } from 'vue';
import {applyTheme} from '@maru/vuetify-theme';
import {DEFAULT_THEME} from "@/constants/theme";
import {useTheme} from 'vuetify';
import { useAppStore } from "@/store/app";
import { useI18n } from 'vue-i18n';
import i18n from '@/plugins/i18n';
import * as Sentry from '@sentry/vue';
import { DialogRoute } from '@maru/hub-vuetify-components';
import webBridge, { ShellStoreState, Theme, useShellStore } from '@maru/shell-app';
import mainBridge, { MainStoreState, useMainStore, MainEvent } from '@maru/main-app';
import { Settings, SETTINGS_LIST } from './constants/Settings';
import { getProjectDraftSettings, getProjectUserRole } from './services/projectSettings';
import { normaliseProjectUserRole } from './constants/roles';
import { hydrateFromSettings as hydratePreviewCommentsFromSettings } from './composables/velt/commentsSettingsState';
import { isEmbedded } from '@/constants/config';
import { ensureLocaleLoaded, isLocaleLoaded } from '@/composables/utils/useRemoteTranslations';

const theme = useTheme();
const shellStore: ShellStoreState = useShellStore();
const mainStore: MainStoreState = useMainStore();
const isEmbeddedIframe = isEmbedded();
// Inside the shell this app is loaded a second time, at /dialog, as the frame m-dialogs render into.
const isDialogFrame = DialogRoute.isActive();
const isAppLoaded = computed(() => {
  // The dialog frame shows none of the survey; its route only has to be mounted.
  if (isDialogFrame) return true;
  if (isEmbeddedIframe) {
    return !appStore.loading && shellStore.initialized && shellStore.orgToken && mainStore.initialized;
  }
  return !appStore.loading
})

if (isEmbeddedIframe) {
  // dialogUrl is what makes the shell set up that frame. The frame sends none of its own, but still needs
  // the handshake: without it the dialog route cannot tell the shell to hide the frame once a dialog closes.
  webBridge.ready({
    store: shellStore,
    ...(isDialogFrame ? {} : { dialogUrl: DialogRoute.url() }),
    onChangeTheme: (newTheme: Theme) => applyTheme.call(this, newTheme, theme),
  });
} else {
  applyTheme.call(undefined, DEFAULT_THEME, theme);
}

// Fetch app data
const appStore = useAppStore();
const { locale } = useI18n();
const userLocale = computed(() => appStore.locale);
const isFirstLoad = ref(!sessionStorage.getItem('firstLoad'));
const initialTranslationsPending = ref(false);
let initialGateDone = false;

const loadTranslations = (targetLocale: string) => {
  const loading = ensureLocaleLoaded(targetLocale);

  // Only the first resolution waits; later switches must not remount the survey view.
  if (initialGateDone) return;
  initialGateDone = true;
  if (isLocaleLoaded(targetLocale)) return;

  initialTranslationsPending.value = true;
  loading.finally(() => {
    initialTranslationsPending.value = false;
  });
};

const pickLocale = (
  available: string[],
  ...candidates: (string | undefined | null)[]
): string => {
  const normalize = (l: string) => l.replace(/-/g, '_');
  const langCode = (l: string) => l.split(/[-_]/)[0].toLowerCase();

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (available.includes(normalize(candidate))) return normalize(candidate);
    const match = available.find(a => langCode(a) === langCode(candidate));
    if (match) return match;
  }

  const english = available.find(a => langCode(a) === 'en');
  if (english) return english;

  return available[0];
}

// Request geolocation permission
const requestGeolocationPermission = async (): Promise<void> => {
  if (!navigator.geolocation) {
    console.warn('Geolocation is not supported by this browser.');
    return;
  }

  try {
    // Check if permissions API is available
    if ('permissions' in navigator) {
      const permission = await navigator.permissions.query({ name: 'geolocation' });

      if (permission.state === 'granted') {
        console.debug('Geolocation permission already granted');
        return;
      }

      if (permission.state === 'denied') {
        console.debug('Geolocation permission denied');
        return;
      }
    }

    // Request permission by attempting to get current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('Geolocation permission granted:', position);
      },
      (error) => {
        console.warn('Geolocation permission denied or error:', error);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0
      }
    );
  } catch (error) {
    console.error('Error requesting geolocation permission:', error);
  }
};

// Resolve the current user's project role, preferring the shell's already-loaded
// project data and falling back to core's authoritative GetProjectById. Safe to
// call repeatedly; no-ops once the role has been resolved.
const resolveUserRole = async (): Promise<void> => {
  // The dialog frame shows no comments, so it needs no role (and no request for it).
  if (isDialogFrame || appStore.userRole) return;

  const shellRole = shellStore.project?.role;
  if (shellRole) {
    appStore.userRole = normaliseProjectUserRole(shellRole);
    return;
  }

  const projectId = shellStore.project?.id;
  if (projectId) {
    const role = await getProjectUserRole(projectId);
    if (role !== null) {
      appStore.userRole = normaliseProjectUserRole(role);
    }
  }
};

// The shell hydrates its project data asynchronously; re-resolve the role
// whenever it (or its role) becomes available so the comments button appears on
// the very first preview rather than only after a second one.
watch(
  () => [shellStore.project?.id, shellStore.project?.role],
  () => { void resolveUserRole(); },
  { immediate: true },
);

onMounted(() => {
  // The dialog frame stays hidden until a dialog opens, so it must not prompt for location.
  if (!isDialogFrame) requestGeolocationPermission();

  if (isEmbeddedIframe && !isDialogFrame) {
    mainBridge.ready({
      store: mainStore,
    }, async (type: MainEvent, payload: any) => {
      if (type === MainEvent.Init) {
        const response = await getProjectDraftSettings(SETTINGS_LIST);
        appStore.projectSettings = Settings.fromApiResponse(response.projectDraftSettingsList);

        // Seed the live preview-comment panel state (which now gates the comment
        // surfaces) from the same draft settings, applying project defaults for
        // any keys a fresh draft hasn't persisted yet. Without this the gates
        // would read the panel's `everyone: false` default until the Settings
        // tab is opened, wrongly hiding comments on embedded runs.
        hydratePreviewCommentsFromSettings(appStore.projectSettings!);

        // Resolve the current user's project role. The shell's project data may
        // not be hydrated yet when Init fires (this is why the comments button
        // was missing on first preview), so resolve it reactively instead of
        // reading once — see the shellStore.project watcher below.
        void resolveUserRole();
      }
    });
  }

  Sentry.setTag('environment', import.meta.env.MODE);
  Sentry.setTag('locale', locale.value);
});



watch(
  () => [appStore.loading, appStore.availableLanguages],
  ([loading, availableLanguages]: [boolean, string[]]) => {
    // check if loading end have available languages
    if (loading || !availableLanguages.length) return;

    let targetLocale: string;
    let priorityLocale: string;
    const browserLocale = navigator.language;

    if (isFirstLoad.value) {
      // Browser language always takes precedence on first load — query param locale
      // (survey base language from the builder) only goes to the backend via startSession
      priorityLocale = null;

      sessionStorage.setItem('firstLoad', 'true');
      isFirstLoad.value = false;
    } else {
      priorityLocale = userLocale.value;
    }

    targetLocale = pickLocale(
      availableLanguages,
      priorityLocale,
      browserLocale,
      i18n.global.locale.value,
    );

    // Sync sessionData.locale.active so the activeLocale getter (used in /do requests)
    // reflects the frontend-resolved locale, not the API's non-fuzzy-matched default
    if (appStore.sessionData?.locale && targetLocale !== appStore.sessionData.locale.active) {
      appStore.sessionData.locale.active = targetLocale;
    }

    appStore.locale = targetLocale;
    i18n.global.locale.value = targetLocale.replace('_', '-') as typeof i18n.global.locale.value;
    loadTranslations(targetLocale);
  },
  { immediate: true }
);

</script>

<style lang="scss">
@import "@/styles/main";
</style>
